import React, { useState, useEffect } from "react";
import RightArrow from "../../Images/Right Arrow.svg";
import { useNavigate } from "react-router-dom";
import calendar from "../../Images/calendar.svg";
import patient from "../../Images/Patients icon.svg";
import line from "../../Images/line.svg";
import Frame_b from "../../Images/Frame_b.svg";
import Frame_y from "../../Images/Frame_yellow.svg";
import { buildApiUrl, getAuthHeaders } from "../../config/api";

const DashboardContent = ({ userData }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseWorkData, setCourseWorkData] = useState([]);
  const [loadingCourseWork, setLoadingCourseWork] = useState(true);
  const [errorCourseWork, setErrorCourseWork] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!userData?.user_id) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/user/${userData.user_id}`), {
          method: "GET",
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch dashboard data");
        }

        const result = await response.json();
        if (result.success) {
          setDashboardData(result.data);
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userData?.user_id]);

  useEffect(() => {
    const fetchCourseWorkData = async () => {
      if (!userData?.user_id) {
        setLoadingCourseWork(false);
        return;
      }

      try {
        const response = await fetch(buildApiUrl("/courses/student/my-work"), {
          method: "GET",
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch course work data");
        }

        const result = await response.json();
        let processedItems = result.items || [];

        // Filter to show only the most recent quiz per course
        const quizItems = processedItems.filter(
          (item) => item.work_type === "QUIZ",
        );
        const otherItems = processedItems.filter(
          (item) => item.work_type !== "QUIZ",
        );

        // Reverse to process most recent first (assuming API returns in chronological order)
        quizItems.reverse();

        const quizMap = {};
        quizItems.forEach((item) => {
          const key = item.course_id;
          if (!quizMap[key]) {
            quizMap[key] = item;
          }
        });

        const filteredQuizzes = Object.values(quizMap);
        processedItems = [...otherItems, ...filteredQuizzes];

        setCourseWorkData(processedItems);
      } catch (err) {
        setErrorCourseWork(err.message);
      } finally {
        setLoadingCourseWork(false);
      }
    };

    fetchCourseWorkData();
  }, [userData?.user_id]);

  const handleNavigate = () => {
    navigate("/myprograms/add-new");
  };

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return "Good Morning";
    if (currentHour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getSubmissionType = (work_type) => {
    switch (work_type) {
      case "DEMO":
        return "Demo";
      case "ASSIGNMENT":
        return "Assignment";
      case "LAB":
        return "Lab";
      case "QUIZ":
        return "Quiz";
      default:
        return work_type;
    }
  };

  const getStatus = (item) => {
    if (item.work_type === "QUIZ") {
      if (item.feedback_status === "Passed") {
        return "Submitted";
      } else if (item.feedback_status === "Failed") {
        return "Retake Quiz";
      } else {
        return "Pending";
      }
    } else {
      return item.feedback_status || "Pending";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const activeItems = courseWorkData.filter(
    (item) => new Date(item.end_date) > new Date(),
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">Loading...</div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">Error: {error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Total Programs Card */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={calendar}
                  alt="calendar"
                  className="w-5 h-5 opacity-80"
                />
                <p className="text-gray-700 text-sm font-medium">
                  Total Programs
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 mb-2">
                <h2 className="text-4xl font-bold text-gray-900">
                  {dashboardData?.dashboard?.enrolled_courses || 0}
                </h2>

                {/* Mini bar chart */}
                <img src={Frame_b} alt="chart" className="flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>

        {/* Completed Programs Card */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={patient}
                  alt="completed"
                  className="w-5 h-5 opacity-80"
                />
                <p className="text-gray-700 text-sm font-medium">
                  Completed Programs
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 mb-2">
                <h2 className="text-4xl font-bold text-gray-900">
                  {dashboardData?.dashboard?.completed_courses || 0}
                </h2>

                {/* Line chart */}
                <div className="w-24 h-14 flex items-center justify-end">
                  <img
                    src={line}
                    alt="line chart"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ongoing Programs Card */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={calendar}
                  alt="ongoing"
                  className="w-5 h-5 opacity-80"
                />
                <p className="text-gray-700 text-sm font-medium">
                  Ongoing Programs
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 mb-2">
                <h2 className="text-4xl font-bold text-gray-900">
                  {dashboardData?.dashboard?.active_courses || 0}
                </h2>

                {/* Mini bar chart */}
                <img src={Frame_y} alt="chart" className="flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Greeting Card */}
      <div className="bg-[#4AACC5] text-white p-6 rounded-xl shadow-sm flex flex-col items-start gap-3">
        <div className="text-3xl font-bold font-['Roboto'] leading-9">
          {getGreeting()}, {userData?.name ? userData.name : "Student"}
        </div>

        <div className="text-base font-normal font-['Roboto']">
          Choose your learning path and start your journey with expert mentors
        </div>

        <div
          className="w-56 px-5 py-2.5 bg-white rounded-[5px] inline-flex justify-center items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={handleNavigate}
        >
          <div className="text-[#4AACC5] text-sm font-bold font-['Roboto'] leading-5 text-center">
            View Program & Courses
          </div>
          <div className="w-4 h-4 relative">
            <div className="w-3 h-3.5 absolute left-[2.33px] top-[1.33px] rounded-sm">
              <img src={RightArrow} alt="Arrow" />
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Table */}
      {/* =========================
          Course Work Table
      ========================== */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-gray-800 font-semibold mb-3">My Course Work</h3>

        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed border-collapse">
            {/* LOCK COLUMN WIDTHS */}
            <colgroup>
              <col className="w-1/4" />
              <col className="w-1/4" />
              <col className="w-1/6" />
              <col className="w-1/6" />
              <col className="w-1/6" />
            </colgroup>

            <thead>
              <tr className="border-b">
                <th className="py-3 px-3 text-left text-gray-600 font-medium">
                  Lesson Name
                </th>
                <th className="py-3 px-3 text-left text-gray-600 font-medium">
                  Course Name
                </th>
                <th className="py-3 px-3 text-left text-gray-600 font-medium">
                  Due Date
                </th>
                <th className="py-3 px-3 text-left text-gray-600 font-medium">
                  Submission Type
                </th>
                <th className="py-3 px-3 text-center text-gray-600 font-medium">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {loadingCourseWork ? (
                <tr>
                  <td
                    colSpan="5"
                    className="py-4 px-3 text-center text-gray-600"
                  >
                    Loading course work...
                  </td>
                </tr>
              ) : errorCourseWork ? (
                <tr>
                  <td
                    colSpan="5"
                    className="py-4 px-3 text-center text-red-500"
                  >
                    Error: {errorCourseWork}
                  </td>
                </tr>
              ) : activeItems.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="py-4 px-3 text-center text-gray-600"
                  >
                    No active course work
                  </td>
                </tr>
              ) : (
                activeItems.map((item, index) => (
                  <tr
                    key={index}
                    className={index < activeItems.length - 1 ? "border-b" : ""}
                  >
                    <td className="py-4 px-3 text-left text-gray-800 truncate">
                      {item.title}
                    </td>
                    <td className="py-4 px-3 text-left text-gray-600 truncate">
                      {item.course_title}
                    </td>
                    <td className="py-4 px-3 text-left text-gray-600">
                      {formatDate(item.end_date)}
                    </td>
                    <td className="py-4 px-3 text-left text-gray-600">
                      {getSubmissionType(item.work_type)}
                    </td>
                    <td className="py-4 px-3 text-center">
                      <span
                        className={`inline-block text-xs font-medium px-3 py-1 rounded-md ${
                          getStatus(item) === "Submitted" ||
                          getStatus(item) === "Given"
                            ? "bg-green-100 text-green-600"
                            : "bg-yellow-100 text-yellow-600"
                        }`}
                      >
                        {getStatus(item)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;